import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { makeStyles } from "tss-react/mui";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";

import ListAltIcon from "@mui/icons-material/ListAlt";

import KMKTable from "../../../Common/Table/KMKTable";

import { Job_Status_Types } from "./JobData";
import Typography from "@mui/material/Typography";
import CopyButton from "../../../Common/CopyButton";

const useStyles = makeStyles()(() => ({
  TableHeading: {
    backgroundColor: "#f0f0f0",
    color: "rgba(0, 0, 0, 0.87)",
    padding: "6px 16px",
    height: "37px",
    fontWeight: "bolder",
  },
  StatusCell: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    alignContent: "center",
  },
}));

function FileOverviewTable({ JobType, data, hideErrRecords, DataSources = [], loadErroredData, jobHistoryId, jobId }) {
  const { classes } = useStyles();

  const columns = useMemo(
    () => [
      {
        id: "FILEPATH",
        name: "File Location",
        sortable: false,
        align: "left",
        width: 150,
        style: { width: "150px", maxWidth: "150px", wordBreak: "break-word" },
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val,
      },
      {
        id: "status",
        name: "Status",
        sortable: false,
        align: "left",
        width: 100,
        style: { width: "100px", maxWidth: "100px", wordBreak: "break-word" },
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          const { statusText = "-", color = "black" } = Job_Status_Types[val];
          return (
            <Box className={classes.StatusCell} sx={{ color }}>
              {statusText}
              {val === "partially" && !hideErrRecords ? (
                <Tooltip title="Show First 50 Error Records" arrow>
                  <IconButton
                    onClick={() => loadErroredData(jobHistoryId, record?.SOURCE_ID || jobId, record.FILEPATH)}
                  >
                    <ListAltIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <></>
              )}
            </Box>
          );
        },
      },
      ...(JobType !== 1
        ? [
            {
              id: "message",
              name: "Message",
              sortable: false,
              align: "left",
              width: 250,
              style: { width: "250px", maxWidth: "250px", wordBreak: "break-word" },
              renderHeadCell: (name) => name,
              renderValueCell: (val, record, index) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="body2" color={Job_Status_Types[record["status"]]}>
                    {val || "-"}
                  </Typography>
                  {!!val && <CopyButton value={val} />}
                </Box>
              ),
            },
          ]
        : []),
    ],
    [JobType, classes.StatusCell, hideErrRecords, loadErroredData, jobHistoryId, jobId]
  );

  return JobType !== 1 ? (
    <Paper sx={{ width: "100%", mb: 2 }}>
      <KMKTable
        loading={false}
        columns={columns}
        noDataText={"No data available"}
        data={data}
        boldHeader
        tableProps={{ size: "small", stickyHeader: true }}
        pagination={false}
        sortingProperties={{}}
        paginationProperties={{}}
        dataIdKey="id"
      />
    </Paper>
  ) : (
    DataSources.map((ds) => (
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Box className={classes.TableHeading}>{ds}</Box>
        <KMKTable
          loading={false}
          columns={columns}
          containerHeight={100}
          noDataText={"No data available"}
          data={data?.filter((file) => file.SOURCE === ds)}
          boldHeader
          tableProps={{ size: "small", stickyHeader: true }}
          pagination={false}
          sortingProperties={{}}
          paginationProperties={{}}
          dataIdKey="id"
        />
      </Paper>
    ))
  );
}

FileOverviewTable.propTypes = {
  JobType: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  loadErroredData: PropTypes.bool.isRequired,
  jobHistoryId: PropTypes.number.isRequired,
  jobId: PropTypes.number.isRequired,
  DataSources: PropTypes.array,
};

export default FileOverviewTable;
